import { APP_ROUTES } from '@/constants/routes';

import type { TPersona } from 'constants/navigation';

type THomeTileID =
  | 'my-applicants'
  | 'all-applicants'
  | 'interviews'
  | 'background-checks'
  | 'further-review'
  | 'final-approval'
  | 'clients'
  | 'singles'
  | 'unread-messages';

type THomeCountSource =
  | 'own-applicants'
  | 'pipeline-applicants'
  | 'own-interviews'
  | 'all-interviews'
  | 'open-checks'
  | 'open-holds'
  | 'final-approval'
  | 'book-clients'
  | 'book-singles'
  | 'all-clients'
  | 'all-singles'
  | 'unread-messages';

type THomeTileSpec = {
  id: THomeTileID;
  source: THomeCountSource;
  href: string;
};

type THomeTile = {
  id: THomeTileID;
  href: string;
  count?: number;
};

const HOME_TILE_LABELS: Record<THomeTileID, string> = {
  'my-applicants': 'My applicants in play',
  'all-applicants': 'All applicants in pipeline',
  'interviews': 'Interviews on your plate',
  'background-checks': 'Background checks open',
  'further-review': 'Held in further review',
  'final-approval': 'Pending final approval',
  'clients': 'Clients in your book',
  'singles': 'Singles in your book',
  'unread-messages': 'Unread messages',
};

const MY_APPLICANTS_TILE: THomeTileSpec = { id: 'my-applicants', source: 'own-applicants', href: APP_ROUTES.myApplicants };

const ALL_APPLICANTS_TILE: THomeTileSpec = { id: 'all-applicants', source: 'pipeline-applicants', href: APP_ROUTES.allApplicants };

const MY_INTERVIEWS_TILE: THomeTileSpec = { id: 'interviews', source: 'own-interviews', href: APP_ROUTES.myInterviews };

const CHECKS_TILE: THomeTileSpec = { id: 'background-checks', source: 'open-checks', href: APP_ROUTES.backgroundCheck };

const FURTHER_REVIEW_TILE: THomeTileSpec = { id: 'further-review', source: 'open-holds', href: APP_ROUTES.furtherReview };

const CONCIERGE_TILES: readonly THomeTileSpec[] = [
  MY_APPLICANTS_TILE,
  ALL_APPLICANTS_TILE,
  MY_INTERVIEWS_TILE,
  CHECKS_TILE,
  FURTHER_REVIEW_TILE,
  { id: 'clients', source: 'book-clients', href: APP_ROUTES.myClients },
  { id: 'singles', source: 'book-singles', href: APP_ROUTES.mySingles },
  { id: 'unread-messages', source: 'unread-messages', href: APP_ROUTES.messages },
];

const HOME_TILES: Record<TPersona, readonly THomeTileSpec[]> = {
  'recruiter': [
    MY_APPLICANTS_TILE,
    MY_INTERVIEWS_TILE,
  ],
  'recruiter_manager': [
    MY_APPLICANTS_TILE,
    ALL_APPLICANTS_TILE,
    MY_INTERVIEWS_TILE,
    CHECKS_TILE,
    FURTHER_REVIEW_TILE,
  ],
  'concierge': CONCIERGE_TILES,
  'concierge_manager': CONCIERGE_TILES,
  'super_admin': [
    { id: 'my-applicants', source: 'pipeline-applicants', href: APP_ROUTES.allApplicants },
    ALL_APPLICANTS_TILE,
    { id: 'interviews', source: 'all-interviews', href: APP_ROUTES.allInterviews },
    CHECKS_TILE,
    FURTHER_REVIEW_TILE,
    { id: 'final-approval', source: 'final-approval', href: APP_ROUTES.finalApproval },
    { id: 'clients', source: 'all-clients', href: APP_ROUTES.memberClients },
    { id: 'singles', source: 'all-singles', href: APP_ROUTES.memberSingles },
  ],
};

const HOME_COPY = {
  greeting: 'Good morning',
  subtitle: 'Your pipeline at a glance — everything below is scoped to what you can see.',
  queuesTitle: 'What needs you',
  queuesDescription: 'Live counts from your queues — click through to work them.',
  noteTitle: 'Every control goes somewhere.',
  noteBody: 'If it looks clickable on these screens, it leads to a real destination in this build. '
    + 'Counts above are live from your scoped queues — they move as you work.',
} as const;

const HOME_NAV_LABEL = 'Home';

export type {
  THomeCountSource,
  THomeTile,
  THomeTileID,
  THomeTileSpec,
};
export {
  HOME_COPY,
  HOME_NAV_LABEL,
  HOME_TILES,
  HOME_TILE_LABELS,
};
